// src/services/dataPersistence/conversationService.ts
// Service for managing the conversation tree

import { 
  ConversationElement, 
  Message, 
  LocationCard,
  WeatherWidget
} from './types';
import { DataSyncService } from './dataSync';

export class ConversationService {
  private dataSyncService: DataSyncService;
  private processingAction: boolean = false;
  private processedElementIds: Set<string> = new Set();
  
  constructor(dataSyncService?: DataSyncService) {
    this.dataSyncService = dataSyncService || DataSyncService.getInstance();
  }
  
  /**
   * Get the entire conversation sequence
   */
  public getConversationSequence(): ConversationElement[] {
    return this.dataSyncService.getConversationData().sequence || [];
  }

  private notifyDataUpdate() {
    window.dispatchEvent(new CustomEvent('tripper-data-updated'));
  }
  
  /**
   * Add a message to the conversation
   */
  public addMessage(message: Message): string {
    console.log('[useChat] Message ID:', message);
    // Store the message
    const serializedMessage = JSON.stringify(message);

    console.log('[ConversationService] serialized message:', serializedMessage);
    
    // Add to conversation sequence
    const element: ConversationElement = {
      id: `msg_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      type: 'message',
      elementId: serializedMessage, // Store the serialized message here (in a real system, we'd store it separately)
      timestamp: message.timestamp
    };
    
    this.dataSyncService.addConversationElement(element);
    this.notifyDataUpdate(); // Add this line
    console.log(`[ConversationService] Added message: ${message.id}`);
    return element.id;
  }

  updateMessage(messageId: string, updates: Partial<any>) {
    const sequence = this.getConversationSequence();
    
    const messageIndex = sequence.findIndex(element => {
      if (element.type === 'message') {
        const msg = JSON.parse(element.elementId);
        return msg.id === messageId;
      }
      return false;
    });
    
    if (messageIndex !== -1) {
      const existingMessage = JSON.parse(sequence[messageIndex].elementId);
      const updatedMessage = { ...existingMessage, ...updates };
      sequence[messageIndex].elementId = JSON.stringify(updatedMessage);
      
      // Save the updated sequence back to localStorage
      const userData = JSON.parse(localStorage.getItem('tripper_user_data') || '{}');
      userData.conversation = userData.conversation || {};
      userData.conversation.sequence = sequence;
      localStorage.setItem('tripper_user_data', JSON.stringify(userData));
    }
  }

  public addLocationCard(
    locationCard: LocationCard, 
    triggerMessageId?: string
  ): string {
    // Log incoming data
    console.log(`[ConversationService] Adding location: ${locationCard.name}`, {
      id: locationCard.id,
      position: locationCard.position
    });
    
    // Use original duplicate detection logic
    if (this.processedElementIds.has(locationCard.id)) {
      console.log(`[ConversationService] Location ${locationCard.id} already exists, skipping`);
      return '';
    }
    
    // Prevent processing if already in progress
    if (this.processingAction) {
      console.log('[ConversationService] Action already in progress, skipping addLocationCard');
      return '';
    }
    
    this.processingAction = true;
    
    try {
      // Create element ID
      const elementId = `loc_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      
      // Ensure position has numeric coordinates
      const position = {
        lat: Number(locationCard.position?.lat || 0),
        lng: Number(locationCard.position?.lng || 0)
      };
      
      console.log(`[ConversationService] Normalized coordinates: lat=${position.lat}, lng=${position.lng}`);
      
      // Create a location with normalized coordinates
      const normalizedLocation = {
        ...locationCard,
        position: position
      };
      
      // Create the element for conversation sequence
      /* const element: any = {
        id: elementId,
        type: 'locationCard',
        elementId: locationCard.id,
        locationName: locationCard.name,
        locationCity: locationCard.city,
        locationCountry: locationCard.country,
        position: position,
        timestamp: new Date().toISOString(),
        messageId: triggerMessageId
      }; */
      // Create the element for conversation sequence
      const element: any = {
        id: elementId,
        type: 'locationCard',
        elementId: locationCard.id,
        locationName: locationCard.name,
        locationCity: locationCard.city,
        locationCountry: locationCard.country,
        position: position,
        timestamp: new Date().toISOString(),
        messageId: triggerMessageId,
        locationType: locationCard.types // Always store the type
      };

      // Add restaurant-specific fields only if it's a restaurant
      if (locationCard.type === 'restaurant') {
        element.cuisine = locationCard.cuisine;
        element.priceLevel = locationCard.priceLevel;
      }

      // Add common optional fields if they exist
      if (locationCard.rating) element.rating = locationCard.rating;
      if (locationCard.reviews) element.reviews = locationCard.reviews;
      if (locationCard.formattedAddress) element.formattedAddress = locationCard.formattedAddress;
      if (locationCard.businessStatus) element.businessStatus = locationCard.businessStatus;
  
      
      console.log('[ConversationService] Sending to API:', JSON.stringify(normalizedLocation));

      // IMPORTANT: Only add to conversation sequence ONCE
      this.dataSyncService.addConversationElement(element as ConversationElement);
  
      // Track this location
      this.processedElementIds.add(locationCard.id);
      this.processedElementIds.add(elementId);
      
      // Check if location already exists in cards array
      const existingCards = this.dataSyncService.getLocationCards();
      const locationExists = existingCards.some(card => card.id === locationCard.id);
      
      // Only add the location card if it doesn't exist
      if (!locationExists) {
        // Add to location collection with normalized coordinates
        const cards = [...existingCards];
        cards.push(normalizedLocation);
        this.dataSyncService.updateLocationCards(cards);
      }
      
      this.notifyDataUpdate();
      console.log(`[ConversationService] Added location card: ${locationCard.id} as ${elementId}`);
      return elementId;
    } finally {
      // Always reset processing flag
      this.processingAction = false;
    }
  }

  public addImageMessage(imageMessage: any): string {
    console.log('[ConversationService] addImageMessage called with:', {
      id: imageMessage.id,
      hasImageUrl: !!imageMessage.imageUrl,
      sender: imageMessage.sender
    });
    
    // Create a deep copy with all properties
    const imageData = {
      id: imageMessage.id,  // Keep the original ID
      content: imageMessage.content || '',
      sender: imageMessage.sender,
      timestamp: imageMessage.timestamp,
      type: 'image',
      imageUrl: imageMessage.imageUrl
    };
    
    // Get current images collection
    const images = [...this.dataSyncService.getImageMessages() || []];
    console.log('[ConversationService] Current images:', images.length);
    
    // Add the new image
    images.push(imageData);
    
    // Update the data store - this will track the change using imageData.id
    this.dataSyncService.updateImageMessages(images);
    
    // Add to conversation sequence
    const element = {
      id: `img_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      type: 'imageMessage',
      elementId: imageMessage.id,  // Reference to the actual image ID
      timestamp: imageMessage.timestamp
    };
    
    console.log('[ConversationService] Adding conversation element:', element);
    this.dataSyncService.addConversationElement(element);
    
    // Notify data update
    this.notifyDataUpdate();
    
    return element.id;
  }


  public addFlightWidget(
    flightWidget: any,
    triggerMessageId?: string
  ): string {
    console.log('[ConversationService] Adding flight widget:', flightWidget.id);
    
    // Add to flight widgets collection (following weather widget pattern)
    const widgets = [...this.dataSyncService.getFlightWidgets()];
    widgets.push(flightWidget);
    this.dataSyncService.updateFlightWidgets(widgets);
    
    // Add to conversation sequence
    const element: ConversationElement = {
      id: `flight_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      type: 'flightWidget',
      elementId: flightWidget.id,  // Just store the ID reference
      timestamp: new Date().toISOString(),
      messageId: triggerMessageId
    };
    
    this.dataSyncService.addConversationElement(element);
    this.notifyDataUpdate();
    
    console.log(`[ConversationService] Added flight widget: ${flightWidget.id}`);
    return element.id;
  }  

    /**
   * Add a weather widget to the conversation
   */
  public addWeatherWidget(
    weatherWidget: WeatherWidget,
    triggerMessageId?: string
  ): string {
    // Add to weather collection
    const widgets = [...this.dataSyncService.getWeatherWidgets()];
    widgets.push(weatherWidget);
    this.dataSyncService.updateWeatherWidgets(widgets);
    
    // Add to conversation sequence
    const element: ConversationElement = {
      id: `weather_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      type: 'weatherWidget',
      elementId: weatherWidget.id,
      timestamp: new Date().toISOString(),
      messageId: triggerMessageId
    };
    
    this.dataSyncService.addConversationElement(element);
    this.notifyDataUpdate();
    console.log(`[ConversationService] Added weather widget: ${weatherWidget.id}`);
    return element.id;
  }
  
  /**
   * Get an element by its ID
   */
  public getElementById(elementId: string): ConversationElement | undefined {
    const sequence = this.getConversationSequence();
    return sequence.find(element => element.id === elementId);
  }
  
  /**
   * Get all elements of a specific type
   */
  public getElementsByType(type: ConversationElement['type']): ConversationElement[] {
    const sequence = this.getConversationSequence();
    return sequence.filter(element => element.type === type);
  }
  
  /**
   * Get all elements triggered by a specific message
   */
  public getElementsByMessageId(messageId: string): ConversationElement[] {
    const sequence = this.getConversationSequence();
    return sequence.filter(element => element.messageId === messageId);
  }
  
  /**
   * Get a specific element's data (the actual message, location card, etc.)
   */
  public getElementData(element: ConversationElement): any {
    switch (element.type) {
      case 'message':
        try {
          return JSON.parse(element.elementId);
        } catch (e) {
          console.error('Failed to parse message data', e);
          return null;
        }
        
      case 'locationCard':
        const locationCards = this.dataSyncService.getLocationCards();
        return locationCards.find(card => card.id === element.elementId);
        
      case 'weatherWidget':
        const weatherWidgets = this.dataSyncService.getWeatherWidgets();
        return weatherWidgets.find(widget => widget.id === element.elementId);

      case 'imageMessage':
        const imageMessages = this.dataSyncService.getImageMessages();
        return imageMessages.find(img => img.id === element.elementId);

      case 'flightWidget':
        const flightWidgets = this.dataSyncService.getFlightWidgets();
        return flightWidgets.find(widget => widget.id === element.elementId);  

      default:
        console.warn(`[ConversationService] Unknown element type: ${element.type}`);
        return null;
    }
  }
  
  /**
   * Get the scroll position
   */
  public getScrollPosition(): number | undefined {
    console.log('[ConversationService] Getting scroll position',this.dataSyncService.getConversationData().lastScrollPosition);
    return this.dataSyncService.getConversationData().lastScrollPosition;
  }
  
  /**
   * Save the scroll position
   */
  public saveScrollPosition(position: number): void {
    this.dataSyncService.updateConversationData({
      lastScrollPosition: position
    });
    //this.notifyDataUpdate();
  }
  
  /**
   * Rebuild the conversation from the sequence
   * This returns all elements in order with their data
   */
  public rebuildConversation(): Array<{
    element: ConversationElement;
    data: any;
  }> {
    const sequence = this.getConversationSequence();
    
    return sequence.map(element => ({
      element,
      data: this.getElementData(element)
    }));
  }
  
  /**
   * Clear the conversation
   */
  public clearConversation(): void {
    this.dataSyncService.updateConversationData({
      sequence: []
    });
    this.notifyDataUpdate();
    console.log('[ConversationService] Cleared conversation');
  }
}
